import React from "react";
import { resolveBookkeepingChange } from "./bookkeepingCurrencyUtils";

export interface BookkeepingCurrency {
  bookkeeping: server.dto.BookkeepingSmall
  currency: server.dto.Currencies
  exchangeRate: number
  isExchangeRateVisible: boolean

  onBookkeepingChange: (newBookkeeping: server.dto.BookkeepingSmall) => void
  onCurrencyChange: (newCurrency: server.dto.Currencies) => void
  onExchangeRateChange: (newExchangeRate: number) => void

  refreshExchangeRate: () => void
};

interface Options {
  onChange?: (bookkeeping: server.dto.BookkeepingSmall, currency: server.dto.Currencies, exchangeRate: number) => void

  /**
     * Change to the bookkeeping's base currency when the bookkeeping changes.
     * Defaults to `true`
     */
  useBaseCurrencyOnBookkeepingChange?: boolean
}

export const useBookkeepingCurrency = (
  initialBookkeeping: server.dto.BookkeepingSmall,
  initialCurrency: server.dto.Currencies,
  initialExchangeRate: number,
  options?: Options
): BookkeepingCurrency => {
  const [bookkeeping, setBookkeeping] = React.useState(initialBookkeeping);
  const [currency, setCurrency] = React.useState(initialCurrency);
  const [exchangeRate, setExchangeRate] = React.useState(initialExchangeRate);

  const useBaseCurrencyOnBookkeepingChange = options?.useBaseCurrencyOnBookkeepingChange ?? true;

  const onBookkeepingCurrencyChange = (bookkeeping: server.dto.BookkeepingSmall, currency: server.dto.Currencies, exchangeRate: number) => {
    setBookkeeping(bookkeeping);
    setCurrency(currency);
    setExchangeRate(exchangeRate);

    if (options?.onChange) {
      options.onChange(bookkeeping, currency, exchangeRate);
    }
  };

  const getIsExchangeRateVisible = (bk: server.dto.BookkeepingSmall, curr: server.dto.Currencies) => (bk != null) && (bk.baseCurrency !== curr);

  const fetchAndSaveExchangeRate = async (bookkeeping: server.dto.BookkeepingSmall, currency: server.dto.Currencies) =>
    window.CoreApi.Bookkeeping.GetExchangeRate({ bookkeepingId: bookkeeping.bookkeepingId, currency })
      .then(r => r.data)
      .then(r => {
        onBookkeepingCurrencyChange(bookkeeping, currency, r.data.rate);
      });

  const isExchangeRateVisible = getIsExchangeRateVisible(bookkeeping, currency);

  const onBookkeepingChange = async (newBookkeeping: server.dto.BookkeepingSmall) => {
    const resolution = await resolveBookkeepingChange(
      newBookkeeping,
      currency,
      exchangeRate,
      !useBaseCurrencyOnBookkeepingChange,
      bookkeeping?.baseCurrency
    );
    onBookkeepingCurrencyChange(newBookkeeping, resolution.currency, resolution.exchangeRate);
  };

  /**
     * When currency changes, we need to fetch the exchange rate IF the exchange rate will be visible in the next render (even if it's already visible)
     * If the exchange rate won't be shown anyway, then we don't need to fetch the exchange rate
     */
  const onCurrencyChange = async (newCurrency: server.dto.Currencies) => {
    // Check if the exchange rate stay/will be visible in the next render
    if (getIsExchangeRateVisible(bookkeeping, newCurrency)) {
      await fetchAndSaveExchangeRate(bookkeeping, newCurrency);
    } else {
      onBookkeepingCurrencyChange(bookkeeping, newCurrency, 1);
    }
  };

  const onExchangeRateChange = (newExchangeRate: number) => {
    onBookkeepingCurrencyChange(bookkeeping, currency, newExchangeRate);
  };

  const refreshExchangeRate = async () => fetchAndSaveExchangeRate(bookkeeping, currency);

  return {
    bookkeeping,
    currency,
    exchangeRate,
    isExchangeRateVisible,
    onBookkeepingChange,
    onCurrencyChange,
    onExchangeRateChange,
    refreshExchangeRate,
  };
};